import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { translate } from '../i18n';
import { getEcho } from '../echo';
import emailService from '../services/emailService';
import { notify } from '../components/CustomToast';

const EmailContext = createContext();

export const useEmail = () => useContext(EmailContext);

export const EmailProvider = ({ children }) => {
    const [accounts, setAccounts] = useState([]);
    const [activeAccountId, setActiveAccountId] = useState(null);
    const [folders, setFolders] = useState([]);
    const [activeFolderId, setActiveFolderId] = useState(null);
    const [emails, setEmails] = useState([]);
    const [activeEmailId, setActiveEmailId] = useState(null);
    const [activeEmail, setActiveEmail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFoldersLoading, setIsFoldersLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, hasMore: false, total: 0 });

    // Refs to prevent duplicate API calls
    const isInitialized = useRef(false);
    const lastAccountId = useRef(null);
    const lastFolderId = useRef(null);

    // ─── Helpers ────────────────────────────────────────────────────────────────

    /**
     * Apply an array of {id, unread_count, message_count} patches to the folders list.
     */
    const applyFolderCountPatches = useCallback((patches) => {
        setFolders(prev => {
            const updated = prev.map(folder => {
                const patch = patches.find(p => p.id === folder.id);
                return patch ? { ...folder, ...patch } : folder;
            });
            // Add any newly-created folders (e.g. Archive folder on first archive action)
            const newFolders = patches.filter(p => !prev.some(f => f.id === p.id));
            return [...updated, ...newFolders];
        });
    }, []);

    /**
     * Optimistically adjust a single folder's counts in local state.
     * delta: { unread_count?: number, message_count?: number }
     */
    const adjustFolderCounts = useCallback((folderId, delta) => {
        if (!folderId) return;
        setFolders(prev => prev.map(folder => {
            if (folder.id !== folderId) return folder;
            return {
                ...folder,
                unread_count: Math.max(0, (folder.unread_count || 0) + (delta.unread_count || 0)),
                message_count: Math.max(0, (folder.message_count || 0) + (delta.message_count || 0)),
            };
        }));
    }, []);

    // ─── Data fetching ───────────────────────────────────────────────────────────

    // Fetch all accounts - only called once on mount
    const fetchAccounts = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await emailService.getAccounts();
            const accountList = response?.data?.accounts || [];
            setAccounts(accountList);

            // Set default account if none selected
            if (accountList.length > 0) {
                const defaultAccount = accountList.find(a => a.is_default) || accountList[0];
                setActiveAccountId(defaultAccount.id);
            }
        } catch (error) {
            console.error('Failed to load mail accounts', error);
            notify('error', 'Failed to load mail accounts');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch folders for a specific account
    const fetchFolders = useCallback(async (accountId) => {
        if (!accountId) return;
        setIsFoldersLoading(true);
        try {
            const response = await emailService.getFolders(accountId);
            const folderList = response?.data?.folders || [];
            setFolders(folderList);

            // Set inbox as default folder
            if (folderList.length > 0) {
                const inbox = folderList.find(f => f.type === 'inbox') || folderList[0];
                setActiveFolderId(inbox.id);
            }
        } catch (error) {
            console.error('Failed to load folders', error);
        } finally {
            setIsFoldersLoading(false);
        }
    }, []);

    // Fetch emails for a specific folder
    const fetchEmails = useCallback(async (folderId, page = 1, append = false) => {
        if (!folderId) return;

        try {
            setIsLoading(true);
            const response = await emailService.getEmails(folderId, { page, per_page: 25 });
            const emailList = response?.data?.emails || [];
            const paginationData = response?.data?.pagination || {};

            if (append) {
                setEmails(prev => [...prev, ...emailList]);
            } else {
                setEmails(emailList);
            }

            setPagination({
                page: paginationData.current_page || page,
                hasMore: paginationData.has_more || false,
                total: paginationData.total || 0,
            });
        } catch (error) {
            console.error('Failed to load emails', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch all unread emails across the account
    const fetchUnreadEmails = useCallback(async () => {
        if (!activeAccountId) return;
        try {
            setIsLoading(true);
            const response = await emailService.search(activeAccountId, '', { is_read: 0 });
            const emailList = response?.data?.emails || [];
            setEmails(emailList);
            setPagination({ page: 1, hasMore: false, total: emailList.length });
        } catch (error) {
            console.error('Failed to load unread emails', error);
        } finally {
            setIsLoading(false);
        }
    }, [activeAccountId]);

    // Fetch all archived emails — finds the Archive folder by name and fetches from it
    const fetchArchivedEmails = useCallback(async () => {
        if (!activeAccountId) return;
        const archiveFolder = folders.find(f => f.name === 'Archive');
        if (!archiveFolder) {
            setEmails([]);
            setPagination({ page: 1, hasMore: false, total: 0 });
            return;
        }
        try {
            setIsLoading(true);
            const response = await emailService.getEmails(archiveFolder.id, { page: 1, per_page: 25 });
            const emailList = response?.data?.emails || [];
            const paginationData = response?.data?.pagination || {};
            setEmails(emailList);
            setPagination({
                page: paginationData.current_page || 1,
                hasMore: paginationData.has_more || false,
                total: paginationData.total || 0,
            });
        } catch (error) {
            console.error('Failed to load archived emails', error);
        } finally {
            setIsLoading(false);
        }
    }, [activeAccountId, folders]);

    // Fetch all starred emails across the account
    const fetchStarredEmails = useCallback(async (append = false) => {
        if (!activeAccountId) return;

        try {
            setIsLoading(true);
            const response = await emailService.getStarredEmails(activeAccountId);
            const emailList = response?.data?.emails || [];

            if (append) {
                setEmails(prev => [...prev, ...emailList]);
            } else {
                setEmails(emailList);
            }
            // If 50 were returned, there may be more (backend limit is 50)
            const hasMore = emailList.length >= 50;
            setPagination({ page: 1, hasMore, total: emailList.length });
        } catch (error) {
            console.error('Failed to load starred emails', error);
        } finally {
            setIsLoading(false);
        }
    }, [activeAccountId]);

    // Fetch single email details
    const fetchEmail = useCallback(async (emailId) => {
        try {
            setIsLoading(true);
            const response = await emailService.getEmail(emailId);
            const email = response?.data?.email;
            setActiveEmail(email);

            // Update read status in list + optimistically fix folder unread count
            if (email) {
                setEmails(prev => prev.map(e => {
                    if (e.id !== emailId) return e;
                    if (!e.is_read) {
                        // Was unread — decrement folder badge immediately
                        adjustFolderCounts(e.folder_id ?? e.mail_folder_id, { unread_count: -1 });
                    }
                    return { ...e, is_read: true };
                }));
            }

            return email;
        } catch (error) {
            console.error('Failed to load email', error);
            notify('error', 'Failed to load email');
        } finally {
            setIsLoading(false);
        }
    }, [adjustFolderCounts]);

    // ─── Actions ─────────────────────────────────────────────────────────────────

    // Track last-fetched email ID to prevent duplicate requests on double-click (H3)
    const loadingEmailIdRef = useRef(null);

    // Select an email — skips fetch if the same email was already loaded
    const selectEmail = useCallback(async (emailId) => {
        if (!emailId) {
            setActiveEmailId(null);
            setActiveEmail(null);
            loadingEmailIdRef.current = null;
            return;
        }
        // H3: Skip if this exact email is already loaded or currently being fetched
        if (loadingEmailIdRef.current === emailId) return;
        loadingEmailIdRef.current = emailId;
        setActiveEmailId(emailId);
        setActiveEmail(null); // Clear immediately so skeleton shows while fetching
        try {
            await fetchEmail(emailId);
            // Intentionally do NOT clear the ref on success — prevents double-click refetch.
            // The ref is reset only when a different email is selected (above) or on error.
        } catch {
            // On error, allow retry by clearing the ref
            if (loadingEmailIdRef.current === emailId) {
                loadingEmailIdRef.current = null;
            }
        }
    }, [fetchEmail]);

    // Mark email as read/unread
    const markAsRead = useCallback(async (emailId, isRead = true) => {
        try {
            // Optimistic update — adjust folder badge before server confirms
            setEmails(prev => prev.map(e => {
                if (e.id !== emailId) return e;
                const wasUnread = !e.is_read;
                const becomingRead = isRead;
                if (wasUnread && becomingRead) {
                    adjustFolderCounts(e.folder_id ?? e.mail_folder_id, { unread_count: -1 });
                } else if (!wasUnread && !becomingRead) {
                    adjustFolderCounts(e.folder_id ?? e.mail_folder_id, { unread_count: +1 });
                }
                return { ...e, is_read: isRead };
            }));
            setActiveEmail(prev => prev?.id === emailId ? { ...prev, is_read: isRead } : prev);

            await emailService.markRead(emailId, isRead);
            // Server will also push .mail.folder.counts.updated via Pusher to confirm
        } catch (error) {
            console.error('Failed to update read status', error);
            // Revert optimistic update on error by re-fetching emails
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [adjustFolderCounts, activeFolderId, fetchEmails]);

    // Toggle star
    const toggleStar = useCallback(async (emailId) => {
        // Optimistic update — flip star before awaiting server
        let previousEmails;
        let previousActiveEmail;
        setEmails(prev => {
            previousEmails = prev;
            const email = prev.find(e => e.id === emailId);
            if (!email) return prev;
            const nowStarred = !email.is_starred;
            if (activeFolderId === '__starred__' && !nowStarred) {
                return prev.filter(e => e.id !== emailId);
            }
            return prev.map(e => e.id === emailId ? { ...e, is_starred: nowStarred } : e);
        });
        setActiveEmail(prev => {
            previousActiveEmail = prev;
            if (prev?.id !== emailId) return prev;
            const nowStarred = !prev.is_starred;
            if (activeFolderId === '__starred__' && !nowStarred) return null;
            return { ...prev, is_starred: nowStarred };
        });

        try {
            await emailService.toggleStar(emailId);
        } catch (error) {
            console.error('Failed to toggle star', error);
            // Revert optimistic update
            setEmails(previousEmails);
            setActiveEmail(previousActiveEmail);
        }
    }, [activeFolderId]);

    // Create a custom mail folder
    const createFolder = useCallback(async (name) => {
        if (!activeAccountId) return;
        const response = await emailService.createFolder(activeAccountId, name);
        const folder = response?.data?.folder;
        if (!folder) throw new Error('No folder returned from API');
        setFolders(prev => [...prev, folder]);
        return folder;
    }, [activeAccountId]);

    // Delete a custom mail folder
    const deleteFolder = useCallback(async (folderId) => {
        await emailService.deleteFolder(folderId);
        setFolders(prev => prev.filter(f => f.id !== folderId));
        setActiveFolderId(prev => {
            if (prev !== folderId) return prev;
            const inbox = folders.find(f => f.type === 'inbox');
            return inbox?.id ?? null;
        });
    }, [folders]);

    // Bulk move emails to a folder
    const bulkMoveEmails = useCallback(async (emailIds, folderId) => {
        if (!emailIds?.length || !folderId) return;
        try {
            setEmails(prev => prev.filter(e => !emailIds.includes(e.id)));
            setActiveEmailId(prev => emailIds.includes(prev) ? null : prev);
            setActiveEmail(prev => emailIds.includes(prev?.id) ? null : prev);
            await emailService.bulkMoveEmails(emailIds, folderId);
            notify('success', `${emailIds.length} email${emailIds.length > 1 ? 's' : ''} moved`);
        } catch (error) {
            console.error('Failed to move emails', error);
            notify('error', 'Failed to move emails');
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [activeFolderId, fetchEmails]);

    // Bulk soft-delete emails
    const bulkDeleteEmails = useCallback(async (emailIds) => {
        if (!emailIds?.length) return;
        try {
            setEmails(prev => prev.filter(e => !emailIds.includes(e.id)));
            setActiveEmailId(prev => emailIds.includes(prev) ? null : prev);
            setActiveEmail(prev => emailIds.includes(prev?.id) ? null : prev);
            await Promise.all(emailIds.map(id => emailService.deleteEmail(id)));
            notify('success', `${emailIds.length} email${emailIds.length > 1 ? 's' : ''} deleted`);
        } catch (error) {
            console.error('Failed to bulk delete emails', error);
            notify('error', 'Failed to delete emails');
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [activeFolderId, fetchEmails]);

    // Bulk archive emails
    const bulkArchiveEmails = useCallback(async (emailIds) => {
        if (!emailIds?.length || !activeAccountId) return;
        try {
            setEmails(prev => prev.filter(e => !emailIds.includes(e.id)));
            setActiveEmailId(prev => emailIds.includes(prev) ? null : prev);
            setActiveEmail(prev => emailIds.includes(prev?.id) ? null : prev);
            await emailService.archiveEmails(activeAccountId, emailIds);
            notify('success', `${emailIds.length} email${emailIds.length > 1 ? 's' : ''} archived`);
        } catch (error) {
            console.error('Failed to bulk archive emails', error);
            notify('error', 'Failed to archive emails');
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [activeAccountId, activeFolderId, fetchEmails]);

    // Archive or unarchive an email. isCurrentlyArchived is passed by the caller
    // based on whether the email's folder name is 'Archive'.
    const toggleArchive = useCallback(async (emailId, isCurrentlyArchived) => {
        if (!activeAccountId) return;
        try {
            setEmails(prev => prev.filter(e => e.id !== emailId));
            setActiveEmailId(prev => prev === emailId ? null : prev);
            setActiveEmail(prev => prev?.id === emailId ? null : prev);

            if (isCurrentlyArchived) {
                await emailService.unarchiveEmails(activeAccountId, [emailId]);
                notify('success', 'Email moved to Inbox');
            } else {
                await emailService.archiveEmails(activeAccountId, [emailId]);
                notify('success', 'Email archived');
            }
        } catch (error) {
            console.error('Failed to toggle archive', error);
            notify('error', 'Failed to archive email');
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [activeAccountId, activeFolderId, fetchEmails]);

    // Delete email (move to trash)
    const deleteEmail = useCallback(async (emailId) => {
        try {
            // Optimistic update — remove from list and fix folder counts
            setEmails(prev => {
                const email = prev.find(e => e.id === emailId);
                if (email) {
                    const folderId = email.folder_id ?? email.mail_folder_id;
                    adjustFolderCounts(folderId, {
                        message_count: -1,
                        unread_count: email.is_read ? 0 : -1,
                    });
                }
                return prev.filter(e => e.id !== emailId);
            });
            setActiveEmailId(prev => prev === emailId ? null : prev);
            setActiveEmail(prev => prev?.id === emailId ? null : prev);

            await emailService.deleteEmail(emailId);
            // Server broadcasts .mail.folder.counts.updated for both source + trash
            notify('success', 'Email moved to trash');
        } catch (error) {
            console.error('Failed to delete email', error);
            notify('error', 'Failed to delete email');
            // Re-fetch to restore correct state
            if (activeFolderId) fetchEmails(activeFolderId, 1, false);
        }
    }, [adjustFolderCounts, activeFolderId, fetchEmails]);

    // Trigger account sync
    const syncAccount = useCallback(async () => {
        if (!activeAccountId) return;

        try {
            setIsSyncing(true);
            const syncResponse = await emailService.syncAccount(activeAccountId);
            const syncError = syncResponse?.data?.sync_error;

            if (syncError) {
                // Server ran but IMAP folder sync failed — show the actual error
                notify('error', translate('messages.email.folder_sync_failed') + ' ' + syncError);
            } else {
                // Folder sync succeeded — refetch from DB immediately
                const folderResponse = await emailService.getFolders(activeAccountId);
                const folderList = folderResponse?.data?.folders || [];
                if (folderList.length > 0) {
                    setFolders(folderList);
                    const stillExists = folderList.find(f => f.id === activeFolderId);
                    if (!stillExists) {
                        const inbox = folderList.find(f => f.type === 'inbox') || folderList[0];
                        setActiveFolderId(inbox.id);
                    }
                }
                notify('success', translate('messages.email.folders_refreshed'));
            }
        } catch (error) {
            console.error('Failed to start sync', error);
            notify('error', 'Failed to start sync');
        } finally {
            setIsSyncing(false);
        }
    }, [activeAccountId, activeFolderId]);

    // Attach new email account (replaces existing)
    const attachAccount = useCallback(async (data) => {
        try {
            setIsLoading(true);
            const response = await emailService.attachEmail(data);
            const newAccount = response?.data?.account;

            if (newAccount) {
                setAccounts(prev => {
                    const idx = prev.findIndex(a => a.id === newAccount.id);
                    return idx >= 0
                        ? prev.map(a => a.id === newAccount.id ? newAccount : a)
                        : [newAccount, ...prev];
                });
                setActiveAccountId(newAccount.id);
                setFolders([]);
                setActiveFolderId(null);
                setEmails([]);
                setPagination({ page: 1, hasMore: false, total: 0 });

                fetchFolders(newAccount.id);
                return newAccount;
            }
        } catch (error) {
            console.error('Failed to attach email', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [fetchFolders]);

    // Manual refresh
    const refreshEmails = useCallback(() => {
        if (activeFolderId === '__starred__') {
            fetchStarredEmails();
        } else if (activeFolderId === '__unread__') {
            fetchUnreadEmails();
        } else if (activeFolderId === '__archived__') {
            fetchArchivedEmails();
        } else if (activeFolderId) {
            fetchEmails(activeFolderId, 1, false);
        }
    }, [activeFolderId, fetchEmails, fetchStarredEmails, fetchUnreadEmails, fetchArchivedEmails]);

    // Load next page (infinite scroll / load more) — starred has no pagination
    const loadMore = useCallback(() => {
        if (pagination.hasMore && !isLoading && activeFolderId !== '__starred__') {
            fetchEmails(activeFolderId, pagination.page + 1, true);
        }
    }, [pagination, isLoading, activeFolderId, fetchEmails]);

    // ─── Mount effects ────────────────────────────────────────────────────────────

    // Fetch accounts once on mount
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            fetchAccounts();
        }
    }, [fetchAccounts]);

    // Fetch folders when account changes
    useEffect(() => {
        if (activeAccountId && activeAccountId !== lastAccountId.current) {
            lastAccountId.current = activeAccountId;
            lastFolderId.current = null;
            setFolders([]);
            setActiveFolderId(null);
            setEmails([]);
            setActiveEmailId(null);
            setActiveEmail(null);
            fetchFolders(activeAccountId);
        }
    }, [activeAccountId, fetchFolders]);

    // Fetch emails when folder changes
    useEffect(() => {
        if (activeFolderId && activeFolderId !== lastFolderId.current) {
            lastFolderId.current = activeFolderId;
            loadingEmailIdRef.current = null;
            setEmails([]);
            setActiveEmailId(null);
            setActiveEmail(null);
            if (activeFolderId === '__starred__') {
                fetchStarredEmails();
            } else if (activeFolderId === '__unread__') {
                fetchUnreadEmails();
            } else if (activeFolderId === '__archived__') {
                fetchArchivedEmails();
            } else {
                fetchEmails(activeFolderId, 1, false);
            }
        }
    }, [activeFolderId, fetchEmails, fetchStarredEmails, fetchUnreadEmails, fetchArchivedEmails]);

    // ─── Pusher real-time listeners ───────────────────────────────────────────────

    useEffect(() => {
        if (!activeAccountId) return;

        const echo = getEcho();
        if (!echo) return;

        const channel = echo.private(`mail.${activeAccountId}`);

        // New email received from server-side sync
        channel.listen('.mail.new', (e) => {
            console.log('New Email Event:', e);
            if (e.email?.folder_id === activeFolderId) {
                setEmails(prev => [e.email, ...prev]);
            }
            // Increment unread badge for the affected folder
            setFolders(prev => prev.map(folder => {
                if (folder.id === e.email?.folder_id) {
                    return {
                        ...folder,
                        unread_count: (folder.unread_count || 0) + 1,
                        message_count: (folder.message_count || 0) + 1,
                    };
                }
                return folder;
            }));
            notify('info', translate('messages.email.new_email_from', { name: e.email?.from?.name || e.email?.from?.email }));
        });

        // Email status updated (is_read, is_starred, etc.)
        channel.listen('.mail.email.updated', (e) => {
            setEmails(prev => prev.map(email =>
                email.id === e.email?.id ? { ...email, ...e.email } : email
            ));
        });

        // Folder counts updated (after mark-read, move, trash from any action)
        channel.listen('.mail.folder.counts.updated', (e) => {
            if (Array.isArray(e.folders) && e.folders.length > 0) {
                applyFolderCountPatches(e.folders);
            }
        });

        // Sync completed — refresh folder counts and email list
        channel.listen('.mail.sync.completed', (e) => {
            setIsSyncing(false);
            if (e.folders) {
                setFolders(prev => prev.map(folder => {
                    const updatedFolder = e.folders.find(f => f.id === folder.id);
                    return updatedFolder ? { ...folder, ...updatedFolder } : folder;
                }));
            }
            if (activeFolderId) {
                fetchEmails(activeFolderId, 1, false);
            }
            notify('success', translate('messages.email.mail_synced'));
        });

        // Email send failed
        channel.listen('.mail.email.send.failed', (e) => {
            notify('error', translate('messages.email.send_failed') + ' ' + (e.error || translate('messages.email.unknown_error')));
            // Update email status in list
            if (e.email_id) {
                setEmails(prev => prev.map(email =>
                    email.id === e.email_id ? { ...email, status: 'failed' } : email
                ));
            }
        });

        return () => {
            channel.stopListening('.mail.new');
            channel.stopListening('.mail.email.updated');
            channel.stopListening('.mail.folder.counts.updated');
            channel.stopListening('.mail.sync.completed');
            channel.stopListening('.mail.email.send.failed');
            const echo = getEcho();
            if (echo) {
                echo.leave(`mail.${activeAccountId}`);
            }
        };
    }, [activeAccountId, activeFolderId, fetchEmails, applyFolderCountPatches]);

    // ─── Context value ────────────────────────────────────────────────────────────

    const activeAccount = accounts.find(a => a.id === activeAccountId) || null;

    const value = {
        // State
        accounts,
        activeAccount,
        activeAccountId,
        folders,
        activeFolderId,
        emails,
        activeEmailId,
        activeEmail,
        isLoading,
        isFoldersLoading,
        isSyncing,
        pagination,

        // Setters
        setActiveAccountId,
        setActiveFolderId,

        // Actions
        fetchAccounts,
        fetchFolders: () => fetchFolders(activeAccountId),
        fetchEmails: () => fetchEmails(activeFolderId, 1, false),
        refreshEmails,
        selectEmail,
        markAsRead,
        toggleStar,
        toggleArchive,
        createFolder,
        deleteFolder,
        bulkMoveEmails,
        bulkDeleteEmails,
        bulkArchiveEmails,
        deleteEmail,
        syncAccount,
        loadMore,
        attachAccount,
    };

    return (
        <EmailContext.Provider value={value}>
            {children}
        </EmailContext.Provider>
    );
};
